<template>
  <div class="tenant-nps-feature">
    <section class="tenant-nps-feature__hero">
      <div>
        <h2>Satisfação por Funcionalidade</h2>
        <p>
          Acompanhe as avaliações das funcionalidades do Pedbot por tenant, período e perfil de nota.
        </p>
      </div>
    </section>

    <section class="tenant-nps-feature__filters">
      <div class="tenant-nps-feature__filter-grid">
        <div class="tenant-nps-feature__field tenant-nps-feature__field--tenant">
          <label>Tenant</label>
          <v-select
            v-model="selectedTenant"
            :options="tenantOptions"
            :filter="filterTenantOptions"
            :clearable="true"
            :reduce="option => option.value"
            :disabled="loadingTenants"
            placeholder="Selecione um tenant"
          />
        </div>

        <div class="tenant-nps-feature__field">
          <label>Funcionalidade</label>
          <v-select
            v-model="selectedFeature"
            :options="featureSelectOptions"
            :clearable="false"
            :disabled="!selectedTenant"
            :reduce="option => option.value"
            placeholder="Todas as features"
          />
        </div>

        <div class="tenant-nps-feature__field">
          <label>Período</label>
          <v-select
            v-model="selectedPeriod"
            :options="periodOptions"
            :clearable="false"
            :reduce="option => option.value"
            placeholder="Últimos 30 dias"
          />
        </div>
      </div>

      <div class="tenant-nps-feature__date-grid">
        <div class="tenant-nps-feature__field">
          <label>Data inicial</label>
          <flat-pickr
            v-model="filters.fromDate"
            :config="flatpickrConfig"
            class="tenant-nps-feature__date-input"
          />
        </div>

        <div class="tenant-nps-feature__field">
          <label>Data final</label>
          <flat-pickr
            v-model="filters.toDate"
            :config="finishAtConfig(flatpickrConfig)"
            :disabled="filters.fromDate === '' || filters.fromDate === null"
            class="tenant-nps-feature__date-input"
          />
        </div>
      </div>
    </section>

    <section v-if="!selectedTenant" class="tenant-nps-feature__empty-state">
      <div class="tenant-nps-feature__empty-card">
        <strong>Selecione um tenant para visualizar as métricas</strong>
        <span>As estatísticas e a listagem detalhada aparecem somente com um tenant filtrado.</span>
      </div>
    </section>

    <template v-else>
      <section class="tenant-nps-feature__card-grid">
        <article
          v-for="card in summaryCards"
          :key="card.key"
          class="tenant-nps-feature__summary-card"
        >
          <span>{{ card.label }}</span>
          <strong :class="card.colorClass">{{ card.value }}</strong>
        </article>
      </section>

      <section class="tenant-nps-feature__panel">
        <div class="tenant-nps-feature__panel-header">
          <h3>Comparativo por Funcionalidade</h3>
        </div>

        <div v-if="comparisonItems.length" class="tenant-nps-feature__comparison-list">
          <div
            v-for="item in comparisonItems"
            :key="item.name"
            class="tenant-nps-feature__comparison-row"
          >
            <div class="tenant-nps-feature__comparison-label">
              {{ item.label }}
            </div>

            <div class="tenant-nps-feature__comparison-track">
              <div
                class="tenant-nps-feature__comparison-fill"
                :style="{ width: comparisonWidth(item.average) }"
              ></div>
            </div>

            <div class="tenant-nps-feature__comparison-value">
              {{ formatAverage(item.average) }}/10
            </div>

            <div class="tenant-nps-feature__comparison-meta">
              <span class="tenant-nps-feature__nps-badge" :class="npsBadgeClass(item.nps_score)">
                {{ formatNpsScore(item.nps_score) }}
              </span>
              <small>{{ formatInteger(item.total_responses) }} respostas</small>
            </div>
          </div>
        </div>

        <div v-else class="tenant-nps-feature__panel-empty">
          Nenhuma avaliação encontrada para o filtro atual.
        </div>
      </section>

      <section class="tenant-nps-feature__details">
        <div class="tenant-nps-feature__details-header">
          <h3>Detalhamento por Atendimento</h3>
        </div>

        <div class="tenant-nps-feature__tabs">
          <button
            v-for="tab in featureTabOptions"
            :key="tab.value === null ? 'all' : tab.value"
            type="button"
            class="tenant-nps-feature__tab"
            :class="{ 'is-active': selectedFeature === tab.value }"
            @click="setSelectedFeature(tab.value)"
          >
            {{ tab.label }}
          </button>
        </div>

        <div class="tenant-nps-feature__table-shell">
          <table class="tenant-nps-feature__table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Funcionalidade</th>
                <th>Cliente</th>
                <th>Nota</th>
                <th>Comentário</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody v-if="tableItems.length">
              <tr v-for="item in tableItems" :key="item.id">
                <td>{{ formatDateCell(item.created_at) }}</td>
                <td>
                  <span class="tenant-nps-feature__feature-chip" :class="featureChipClass(item.feature)">
                    {{ featureLabel(item.feature) }}
                  </span>
                </td>
                <td>{{ item.user && item.user.name ? item.user.name : '-' }}</td>
                <td>
                  <span class="tenant-nps-feature__grade" :class="gradeClass(item.grade)">
                    {{ item.grade }}/10
                  </span>
                </td>
                <td>
                  <span
                    class="tenant-nps-feature__comment"
                    :title="item.open_field || ''"
                  >
                    {{ item.open_field || '—' }}
                  </span>
                </td>
                <td>
                  <span class="tenant-nps-feature__status-chip" :class="statusChipClass(item.grade)">
                    {{ statusLabel(item.grade) }}
                  </span>
                </td>
              </tr>
            </tbody>

            <tbody v-else>
              <tr>
                <td colspan="6" class="tenant-nps-feature__table-empty">
                  Nenhum registro encontrado para os filtros selecionados.
                </td>
              </tr>
            </tbody>
          </table>

          <div class="tenant-nps-feature__table-footer">
            <span>{{ paginationSummary }}</span>

            <div class="tenant-nps-feature__pagination">
              <button
                type="button"
                class="tenant-nps-feature__page-button"
                :disabled="pagination.page <= 1 || loadingList"
                @click="goToPage(pagination.page - 1)"
              >
                ‹
              </button>

              <button
                v-for="page in visiblePages"
                :key="page"
                type="button"
                class="tenant-nps-feature__page-button"
                :class="{ 'is-active': page === pagination.page }"
                :disabled="loadingList"
                @click="goToPage(page)"
              >
                {{ page }}
              </button>

              <button
                type="button"
                class="tenant-nps-feature__page-button"
                :disabled="pagination.page >= pagination.totalPages || loadingList"
                @click="goToPage(pagination.page + 1)"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import 'flatpickr/dist/flatpickr.css';
import flatPickr from 'vue-flatpickr-component';
import { Portuguese } from 'flatpickr/dist/l10n/pt.js';
import tenantsRequest from '@/http/requests/tenants';
import npsFeatureRequest from '@/http/requests/npsFeature';
import { filterTenantsByHost, formatDate } from '@/utils';

const FEATURE_META = {
  maria: { label: 'MarIA', color: 'is-maria' },
  'chat-attendance': { label: 'Chat de Atendimentos', color: 'is-chat-attendance' },
  orders: { label: 'Gestão de Pedidos', color: 'is-orders' },
  kanban: { label: 'Kanban Generalista', color: 'is-kanban' },
};

export default {
  name: 'tenant-nps-feature',
  components: {
    flatPickr,
    vSelect,
  },
  data() {
    return {
      loadingTenants: false,
      loadingMetrics: false,
      loadingList: false,
      tenants: [],
      metrics: {
        features: [],
        total_responses: 0,
        overall_average: 0,
        overall_nps_score: 0,
      },
      listResponse: {
        items: [],
        total_items: 0,
        total_pages: 0,
      },
      selectedTenant: null,
      selectedFeature: null,
      selectedPeriod: 'last-30-days',
      isApplyingPeriodPreset: false,
      filters: {
        fromDate: null,
        toDate: null,
      },
      pagination: {
        page: 1,
        limit: 6,
        totalPages: 0,
        totalItems: 0,
      },
      periodOptions: [
        { label: 'Últimos 7 dias', value: 'last-7-days' },
        { label: 'Últimos 30 dias', value: 'last-30-days' },
        { label: 'Últimos 90 dias', value: 'last-90-days' },
        { label: 'Personalizado', value: 'custom' },
      ],
      flatpickrConfig: {
        enableTime: false,
        noCalendar: false,
        locale: Portuguese,
        dateFormat: 'd/m/Y',
        allowInput: false,
      },
    };
  },
  computed: {
    authToken() {
      return localStorage.getItem('accessToken') || '';
    },
    tenantOptions() {
      return this.tenants.map((tenant) => ({
        label: this.tenantOptionLabel(tenant),
        value: this.tenantOptionValue(tenant),
        searchText: this.tenantSearchText(tenant),
      }));
    },
    featureOptions() {
      const metricsFeatures = Array.isArray(this.metrics.features) && this.metrics.features.length
        ? this.metrics.features
        : Object.keys(FEATURE_META).map((name) => ({ name, label: FEATURE_META[name].label }));

      return metricsFeatures.map((feature) => ({
        label: feature.label,
        value: feature.name,
      }));
    },
    featureSelectOptions() {
      return [
        { label: 'Todas as features', value: null },
        ...this.featureOptions,
      ];
    },
    featureTabOptions() {
      return this.featureSelectOptions.map((option) => ({
        label: option.value === null ? 'Todas' : option.label,
        value: option.value,
      }));
    },
    selectedMetric() {
      if (!this.selectedFeature) {
        return null;
      }

      return this.metrics.features.find((item) => item.name === this.selectedFeature) || null;
    },
    comparisonItems() {
      if (!Array.isArray(this.metrics.features)) {
        return [];
      }

      if (this.selectedFeature) {
        return this.selectedMetric ? [this.selectedMetric] : [];
      }

      return this.metrics.features;
    },
    summaryCards() {
      if (!this.selectedTenant) {
        return [];
      }

      if (this.selectedMetric) {
        return [
          {
            key: 'average',
            label: `Média ${this.selectedMetric.label}`,
            value: this.formatAverage(this.selectedMetric.average),
            colorClass: 'is-maria-accent',
          },
          {
            key: 'promoters',
            label: 'Promotores',
            value: `${this.formatPercentage(this.selectedMetric.promoters_percentage)}%`,
            colorClass: 'is-promoter',
          },
          {
            key: 'neutrals',
            label: 'Neutros',
            value: `${this.formatPercentage(this.selectedMetric.neutrals_percentage)}%`,
            colorClass: 'is-neutral',
          },
          {
            key: 'detractors',
            label: 'Detratores',
            value: `${this.formatPercentage(this.selectedMetric.detractors_percentage)}%`,
            colorClass: 'is-detractor',
          },
        ];
      }

      const cards = this.metrics.features.map((feature) => ({
        key: feature.name,
        label: feature.label,
        value: this.formatInteger(feature.total_responses),
        colorClass: 'is-primary',
      }));

      cards.push({
        key: 'responses',
        label: 'Respostas coletadas',
        value: this.formatInteger(this.metrics.total_responses),
        colorClass: 'is-dark',
      });

      return cards;
    },
    tableItems() {
      return this.listResponse.items || [];
    },
    paginationSummary() {
      if (!this.pagination.totalItems) {
        return 'Mostrando 0 registros';
      }

      const start = ((this.pagination.page - 1) * this.pagination.limit) + 1;
      const end = Math.min(this.pagination.page * this.pagination.limit, this.pagination.totalItems);

      return `Mostrando ${start}-${end} de ${this.pagination.totalItems} registros`;
    },
    visiblePages() {
      if (!this.pagination.totalPages) {
        return [1];
      }

      const pages = [];
      const start = Math.max(this.pagination.page - 2, 1);
      const end = Math.min(start + 4, this.pagination.totalPages);

      for (let page = start; page <= end; page += 1) {
        pages.push(page);
      }

      return pages;
    },
  },
  watch: {
    selectedTenant() {
      this.pagination.page = 1;
      this.fetchAllData();
    },
    selectedFeature() {
      this.pagination.page = 1;
      this.fetchPaginatedList();
    },
    selectedPeriod(value) {
      this.applyPeriodPreset(value);
      this.pagination.page = 1;
      this.fetchAllData();
    },
    'filters.fromDate'() {
      if (this.isApplyingPeriodPreset) {
        return;
      }

      if (this.selectedPeriod !== 'custom') {
        this.selectedPeriod = 'custom';
        return;
      }

      this.pagination.page = 1;
      this.fetchAllData();
    },
    'filters.toDate'() {
      if (this.isApplyingPeriodPreset) {
        return;
      }

      if (this.selectedPeriod !== 'custom') {
        this.selectedPeriod = 'custom';
        return;
      }

      this.pagination.page = 1;
      this.fetchAllData();
    },
  },
  created() {
    this.applyPeriodPreset(this.selectedPeriod);
    this.loadTenants();
  },
  methods: {
    tenantOptionValue(tenant) {
      if (!tenant) {
        return null;
      }

      return tenant.fqdn ? tenant.fqdn.split('.')[0] : tenant.id;
    },
    tenantOptionLabel(tenant) {
      if (!tenant) {
        return '';
      }

      return String(this.tenantOptionValue(tenant));
    },
    tenantSearchText(tenant) {
      if (!tenant) {
        return '';
      }

      return String(this.tenantOptionValue(tenant));
    },
    normalizeTenantSearchValue(value) {
      return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    },
    filterTenantOptions(options, search) {
      const normalizedSearch = this.normalizeTenantSearchValue(search);
      if (!normalizedSearch) {
        return options;
      }

      return options.filter((option) => {
        const haystack = this.normalizeTenantSearchValue(
          option && option.searchText ? option.searchText : option.label
        );

        return haystack.includes(normalizedSearch);
      });
    },
    async loadTenants() {
      this.loadingTenants = true;

      try {
        const response = await tenantsRequest.getAllTenants(this.authToken, false);
        this.tenants = filterTenantsByHost(response || []);
      } catch (error) {
        this.$vs.notify({
          color: 'danger',
          title: 'Não foi possível carregar os tenants',
          text: 'Tente novamente em alguns instantes.',
        });
      } finally {
        this.loadingTenants = false;
      }
    },
    applyPeriodPreset(period) {
      const today = new Date();
      const format = (date) => {
        const day = `${date.getDate()}`.padStart(2, '0');
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      if (period === 'custom') {
        return;
      }

      const days = {
        'last-7-days': 6,
        'last-30-days': 29,
        'last-90-days': 89,
      }[period] || 29;

      const start = new Date(today);
      start.setDate(today.getDate() - days);

      this.isApplyingPeriodPreset = true;
      this.filters.fromDate = format(start);
      this.filters.toDate = format(today);
      this.$nextTick(() => {
        this.isApplyingPeriodPreset = false;
      });
    },
    finishAtConfig(config) {
      return {
        ...config,
        minDate: this.filters.fromDate,
      };
    },
    buildParams() {
      return {
        tenantName: this.selectedTenant,
        fromDate: this.parseDateToApi(this.filters.fromDate),
        toDate: this.parseDateToApi(this.filters.toDate),
      };
    },
    parseDateToApi(dateString) {
      if (!dateString) {
        return null;
      }

      const parts = dateString.split('/');

      if (parts.length !== 3) {
        return dateString;
      }

      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    },
    async fetchAllData() {
      if (!this.selectedTenant || !this.filters.fromDate || !this.filters.toDate) {
        this.resetData();
        return;
      }

      await Promise.all([
        this.fetchMetrics(),
        this.fetchPaginatedList(),
      ]);
    },
    async fetchMetrics() {
      if (!this.selectedTenant) {
        this.metrics = {
          features: [],
          total_responses: 0,
          overall_average: 0,
          overall_nps_score: 0,
        };
        return;
      }

      this.loadingMetrics = true;

      try {
        const response = await npsFeatureRequest.getMetrics(this.authToken, this.buildParams());
        this.metrics = this.normalizeMetricsResponse(response.data);

        if (this.selectedFeature && !this.metrics.features.some((feature) => feature.name === this.selectedFeature)) {
          this.selectedFeature = null;
        }
      } catch (error) {
        this.$vs.notify({
          color: 'danger',
          title: 'Não foi possível carregar as métricas',
          text: 'Tente novamente em alguns instantes.',
        });
      } finally {
        this.loadingMetrics = false;
      }
    },
    async fetchPaginatedList() {
      if (!this.selectedTenant) {
        this.resetList();
        return;
      }

      this.loadingList = true;

      try {
        const response = await npsFeatureRequest.getPaginated(this.authToken, {
          ...this.buildParams(),
          feature: this.selectedFeature,
          paginationPage: this.pagination.page,
          paginationLimit: this.pagination.limit,
          sortName: 'created_at',
          sortType: 'DESC',
        });

        this.listResponse = this.normalizeListResponse(response.data);

        this.pagination.totalPages = this.listResponse.total_pages || 0;
        this.pagination.totalItems = this.listResponse.total_items || 0;
      } catch (error) {
        this.$vs.notify({
          color: 'danger',
          title: 'Não foi possível carregar a listagem',
          text: 'Tente novamente em alguns instantes.',
        });
      } finally {
        this.loadingList = false;
      }
    },
    resetData() {
      this.metrics = this.normalizeMetricsResponse();
      this.resetList();
    },
    resetList() {
      this.listResponse = this.normalizeListResponse();
      this.pagination.totalPages = 0;
      this.pagination.totalItems = 0;
    },
    normalizeMetricsResponse(data = {}) {
      return {
        features: Array.isArray(data.features) ? data.features : [],
        total_responses: Number(data.total_responses || 0),
        overall_average: Number(data.overall_average || 0),
        overall_nps_score: Number(data.overall_nps_score || 0),
      };
    },
    normalizeListResponse(data = {}) {
      return {
        items: Array.isArray(data.items) ? data.items : [],
        total_items: Number(data.total_items || 0),
        total_pages: Number(data.total_pages || 0),
      };
    },
    setSelectedFeature(feature) {
      this.selectedFeature = feature;
    },
    goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.pagination.page) {
        return;
      }

      this.pagination.page = page;
      this.fetchPaginatedList();
    },
    formatAverage(value) {
      return Number(value || 0).toFixed(1);
    },
    formatPercentage(value) {
      return Number(value || 0).toFixed(0);
    },
    formatInteger(value) {
      return new Intl.NumberFormat('pt-BR').format(Number(value || 0));
    },
    formatNpsScore(value) {
      const parsed = Number(value || 0);
      return `${parsed >= 0 ? '+' : ''}${parsed}`;
    },
    comparisonWidth(value) {
      const safeValue = Math.max(Math.min(Number(value || 0), 10), 0);
      return `${safeValue * 10}%`;
    },
    featureLabel(feature) {
      return FEATURE_META[feature] ? FEATURE_META[feature].label : feature;
    },
    featureChipClass(feature) {
      return FEATURE_META[feature] ? FEATURE_META[feature].color : '';
    },
    formatDateCell(date) {
      return formatDate(date).date_only || '-';
    },
    gradeProfile(grade) {
      if (grade >= 9) {
        return 'promoter';
      }

      if (grade >= 7) {
        return 'neutral';
      }

      return 'detractor';
    },
    statusLabel(grade) {
      return {
        promoter: 'Promotor',
        neutral: 'Neutro',
        detractor: 'Detrator',
      }[this.gradeProfile(grade)];
    },
    statusChipClass(grade) {
      return `is-${this.gradeProfile(grade)}`;
    },
    gradeClass(grade) {
      return `is-${this.gradeProfile(grade)}`;
    },
    npsBadgeClass(score) {
      if (score >= 50) {
        return 'is-promoter';
      }

      if (score >= 0) {
        return 'is-neutral';
      }

      return 'is-detractor';
    },
  },
};
</script>

<style scoped>
.tenant-nps-feature {
  padding: 8px 6px 24px;
  padding-top: 0;
}

.tenant-nps-feature__hero,
.tenant-nps-feature__filters,
.tenant-nps-feature__panel,
.tenant-nps-feature__details,
.tenant-nps-feature__empty-card {
  background: #fff;
  border: 1px solid #dfe7f2;
  border-radius: 16px;
  box-shadow: 0 14px 40px rgba(15, 34, 58, 0.04);
}

.tenant-nps-feature__hero {
  margin-bottom: 18px;
  padding: 18px 22px;
}

.tenant-nps-feature__hero h2,
.tenant-nps-feature__panel-header h3,
.tenant-nps-feature__details-header h3 {
  margin: 0;
  color: #2f343a;
  font-size: 16px;
  font-weight: 700;
}

.tenant-nps-feature__hero p {
  margin: 6px 0 0;
  color: #6f7f92;
  font-size: 13px;
}

.tenant-nps-feature__filters {
  margin-bottom: 22px;
  padding: 16px 20px;
}

.tenant-nps-feature__filter-grid,
.tenant-nps-feature__date-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.tenant-nps-feature__date-grid {
  margin-top: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.tenant-nps-feature__field label {
  display: block;
  margin-bottom: 6px;
  color: #6f7f92;
  font-size: 12px;
  font-weight: 600;
}

.tenant-nps-feature__field--tenant {
  grid-column: span 1;
}

.tenant-nps-feature__date-input {
  display: block;
  width: 100%;
}

.tenant-nps-feature__date-input ::v-deep input {
  width: 100%;
  height: 38px;
  min-height: 38px;
  padding: 8px 10px;
  border: 1px solid #d7e1ef;
  border-radius: 10px;
  background: #fff;
  color: #2f343a;
  box-sizing: border-box;
  line-height: 1.2;
}

.tenant-nps-feature__date-input ::v-deep input:focus {
  border-color: #1886f5;
  box-shadow: 0 0 0 3px rgba(24, 134, 245, 0.12);
  outline: none;
}

.tenant-nps-feature__filters ::v-deep .vs__dropdown-toggle {
  min-height: 38px;
  border: 1px solid #d7e1ef;
  border-radius: 10px;
  background: #fff;
  padding: 2px 8px;
}

.tenant-nps-feature__filters ::v-deep .vs__selected-options {
  padding: 0;
}

.tenant-nps-feature__filters ::v-deep .vs__selected,
.tenant-nps-feature__filters ::v-deep .vs__search,
.tenant-nps-feature__filters ::v-deep .vs__search::placeholder {
  color: #2f343a;
  font-size: 13px;
}

.tenant-nps-feature__filters ::v-deep .vs__actions {
  padding: 0 2px 0 8px;
}

.tenant-nps-feature__filters ::v-deep .vs--disabled .vs__dropdown-toggle,
.tenant-nps-feature__filters ::v-deep .vs--disabled .vs__search,
.tenant-nps-feature__filters ::v-deep .vs--disabled .vs__selected,
.tenant-nps-feature__filters ::v-deep .vs--disabled .vs__actions,
.tenant-nps-feature__filters ::v-deep .vs--disabled .vs__open-indicator {
  background: #fff;
}

.tenant-nps-feature__filters ::v-deep .vs--disabled .vs__selected,
.tenant-nps-feature__filters ::v-deep .vs--disabled .vs__search::placeholder {
  color: #7c8da3;
}

.tenant-nps-feature__filters ::v-deep .vs--disabled .vs__open-indicator {
  fill: #7c8da3;
}

.tenant-nps-feature__filters ::v-deep .vs__dropdown-toggle:focus-within {
  border-color: #1886f5;
  box-shadow: 0 0 0 3px rgba(24, 134, 245, 0.12);
  outline: none;
}

.tenant-nps-feature__card-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.tenant-nps-feature__summary-card {
  background: #fff;
  border: 1px solid #dfe7f2;
  border-radius: 14px;
  padding: 12px 16px;
  min-height: 76px;
}

.tenant-nps-feature__summary-card span {
  display: block;
  color: #6d7480;
  font-size: 12px;
}

.tenant-nps-feature__summary-card strong {
  display: block;
  margin-top: 8px;
  color: #1886f5;
  font-size: 23px;
  font-weight: 700;
}

.tenant-nps-feature__summary-card strong.is-dark {
  color: #2f343a;
}

.tenant-nps-feature__summary-card strong.is-promoter {
  color: #22c55e;
}

.tenant-nps-feature__summary-card strong.is-neutral {
  color: #fb923c;
}

.tenant-nps-feature__summary-card strong.is-detractor {
  color: #ef4444;
}

.tenant-nps-feature__summary-card strong.is-maria-accent {
  color: #6d5efc;
}

.tenant-nps-feature__summary-card strong.is-primary {
  color: #1886f5;
}

.tenant-nps-feature__panel,
.tenant-nps-feature__details {
  margin-bottom: 14px;
  padding: 14px 16px;
}

.tenant-nps-feature__comparison-list {
  margin-top: 10px;
}

.tenant-nps-feature__comparison-row {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr) 90px 180px;
  gap: 12px;
  align-items: center;
  padding: 12px 0;
  border-top: 1px solid #ebf0f7;
}

.tenant-nps-feature__comparison-row:first-child {
  border-top: 0;
}

.tenant-nps-feature__comparison-label,
.tenant-nps-feature__comparison-value {
  color: #2f343a;
  font-weight: 600;
  font-size: 13px;
}

.tenant-nps-feature__comparison-track {
  position: relative;
  height: 8px;
  border-radius: 999px;
  background: #eef2f7;
  overflow: hidden;
}

.tenant-nps-feature__comparison-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #1886f5 0%, #2aa0ff 100%);
}

.tenant-nps-feature__comparison-value {
  color: #1886f5;
}

.tenant-nps-feature__comparison-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.tenant-nps-feature__comparison-meta small {
  color: #b2bdcb;
  font-size: 12px;
}

.tenant-nps-feature__nps-badge,
.tenant-nps-feature__status-chip,
.tenant-nps-feature__feature-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 58px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
}

.tenant-nps-feature__nps-badge.is-promoter,
.tenant-nps-feature__status-chip.is-promoter {
  background: #22c55e;
}

.tenant-nps-feature__nps-badge.is-neutral,
.tenant-nps-feature__status-chip.is-neutral {
  background: #fb923c;
}

.tenant-nps-feature__nps-badge.is-detractor,
.tenant-nps-feature__status-chip.is-detractor {
  background: #ef4444;
}

.tenant-nps-feature__feature-chip.is-maria {
  background: #6d5efc;
}

.tenant-nps-feature__feature-chip.is-chat-attendance {
  background: #1886f5;
}

.tenant-nps-feature__feature-chip.is-orders {
  background: #fb923c;
}

.tenant-nps-feature__feature-chip.is-kanban {
  background: #ef4444;
}

.tenant-nps-feature__details-header {
  margin-bottom: 12px;
}

.tenant-nps-feature__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  padding: 10px;
  border: 1px solid #dfe7f2;
  border-radius: 12px;
  background: #fff;
}

.tenant-nps-feature__tab,
.tenant-nps-feature__page-button {
  border: 1px solid #d7e1ef;
  background: #fff;
  color: #64748b;
  cursor: pointer;
  transition: 0.2s ease;
}

.tenant-nps-feature__tab {
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 13px;
}

.tenant-nps-feature__tab.is-active,
.tenant-nps-feature__page-button.is-active,
.tenant-nps-feature__tab:hover,
.tenant-nps-feature__page-button:hover:not(:disabled) {
  border-color: #1886f5;
  background: #1886f5;
  color: #fff;
}

.tenant-nps-feature__table-shell {
  border: 1px solid #dfe7f2;
  border-radius: 12px;
  overflow: hidden;
}

.tenant-nps-feature__table {
  width: 100%;
  border-collapse: collapse;
}

.tenant-nps-feature__table th,
.tenant-nps-feature__table td {
  padding: 11px 14px;
  border-bottom: 1px solid #edf2f7;
  text-align: left;
}

.tenant-nps-feature__table th {
  color: #667085;
  font-size: 12px;
  font-weight: 700;
  background: #fbfcfe;
}

.tenant-nps-feature__table td {
  color: #2f343a;
  font-size: 13px;
}

.tenant-nps-feature__grade {
  font-weight: 700;
}

.tenant-nps-feature__grade.is-promoter {
  color: #22c55e;
}

.tenant-nps-feature__grade.is-neutral {
  color: #fb923c;
}

.tenant-nps-feature__grade.is-detractor {
  color: #ef4444;
}

.tenant-nps-feature__comment {
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.tenant-nps-feature__table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  color: #6d7480;
  font-size: 12px;
}

.tenant-nps-feature__pagination {
  display: flex;
  gap: 4px;
}

.tenant-nps-feature__page-button {
  min-width: 26px;
  height: 26px;
  border-radius: 6px;
  font-size: 12px;
}

.tenant-nps-feature__page-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tenant-nps-feature__empty-state {
  margin-bottom: 18px;
}

.tenant-nps-feature__empty-card,
.tenant-nps-feature__panel-empty,
.tenant-nps-feature__table-empty {
  text-align: center;
  color: #6f7f92;
}

.tenant-nps-feature__empty-card {
  padding: 30px 20px;
}

.tenant-nps-feature__empty-card strong {
  display: block;
  margin-bottom: 10px;
  color: #2f343a;
}

.tenant-nps-feature__panel-empty {
  padding: 24px 0 8px;
}

.tenant-nps-feature__table-empty {
  padding: 32px 16px;
}

@media (max-width: 1200px) {
  .tenant-nps-feature__card-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .tenant-nps-feature__comparison-row {
    grid-template-columns: 1fr;
  }

  .tenant-nps-feature__comparison-meta {
    justify-content: flex-start;
  }
}

@media (max-width: 960px) {
  .tenant-nps-feature__filter-grid,
  .tenant-nps-feature__date-grid,
  .tenant-nps-feature__card-grid {
    grid-template-columns: 1fr;
  }

  .tenant-nps-feature__table-shell {
    overflow-x: auto;
  }

  .tenant-nps-feature__table {
    min-width: 760px;
  }

  .tenant-nps-feature__table-footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>